import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { financeApi } from '../../api/finance';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { IconActionButton } from '../../components/IconActionButton';
import { Lock, Unlock } from 'lucide-react';
import { DataTable } from '../../components/DataTable';
import { usePermission } from '../../auth/AuthContext';
import { useToast } from '../../components/Toast';

export function FiscalYearsPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const canReopen = usePermission('finance.periods.reopen');
  const [showForm, setShowForm] = useState(false);
  const [expandedYearId, setExpandedYearId] = useState<number | null>(null);

  const fiscalYearsQuery = useQuery({ queryKey: ['finance-fiscal-years'], queryFn: () => financeApi.listFiscalYears() });
  const yearDetailQuery = useQuery({ queryKey: ['finance-fiscal-year-detail', expandedYearId], queryFn: () => financeApi.getFiscalYear(expandedYearId!), enabled: !!expandedYearId });

  const { register, handleSubmit, reset } = useForm<{ yearLabel: string; startDate: string; endDate: string }>();
  const createMutation = useMutation({
    mutationFn: (v: { yearLabel: string; startDate: string; endDate: string }) => financeApi.createFiscalYear(v),
    onSuccess: () => { showSuccess('Fiscal year created with 12 monthly periods.'); queryClient.invalidateQueries({ queryKey: ['finance-fiscal-years'] }); setShowForm(false); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create fiscal year.'),
  });

  const closeYearMutation = useMutation({
    mutationFn: (id: number) => financeApi.setFiscalYearStatus(id, 'closed'),
    onSuccess: () => { showSuccess('Fiscal year closed.'); queryClient.invalidateQueries({ queryKey: ['finance-fiscal-years'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  const closePeriodMutation = useMutation({
    mutationFn: (periodId: number) => financeApi.closePeriod(periodId),
    onSuccess: () => { showSuccess('Period closed.'); queryClient.invalidateQueries({ queryKey: ['finance-fiscal-year-detail', expandedYearId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  const reopenPeriodMutation = useMutation({
    mutationFn: (periodId: number) => financeApi.reopenPeriod(periodId),
    onSuccess: () => { showSuccess('Period reopened.'); queryClient.invalidateQueries({ queryKey: ['finance-fiscal-year-detail', expandedYearId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  return (
    <div className="page">
      <PageHeader title="Fiscal Years & Accounting Periods" actions={<button className="btn-primary" onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : 'Create Fiscal Year'}</button>} />

      {showForm && (
        <section className="card">
          <h2>New Fiscal Year</h2>
          <p className="muted">12 monthly accounting periods are generated automatically.</p>
          <form onSubmit={handleSubmit((v) => createMutation.mutate(v))}>
            <label>Year Label<input {...register('yearLabel', { required: true })} placeholder="FY2026" /></label>
            <label>Start Date<input type="date" {...register('startDate', { required: true })} /></label>
            <label>End Date<input type="date" {...register('endDate', { required: true })} /></label>
            <div className="form-actions"><button type="submit" className="btn-primary" disabled={createMutation.isPending}>{createMutation.isPending ? 'Creating…' : 'Create'}</button><button type="button" className="btn-secondary" onClick={() => { setShowForm(false); reset(); }}>Cancel</button></div>
          </form>
        </section>
      )}

      <section className="card">
        {fiscalYearsQuery.data && (
          <DataTable
            rows={fiscalYearsQuery.data.data}
            rowKey={(fy) => fy.id}
            emptyMessage="No fiscal years yet."
            columns={[
              { header: 'Year', render: (fy) => fy.year_label },
              { header: 'Start', render: (fy) => fy.start_date },
              { header: 'End', render: (fy) => fy.end_date },
              { header: 'Status', render: (fy) => <StatusBadge status={fy.status} /> },
              {
                header: '',
                render: (fy) => (
                  <span className="row-actions">
                    <button className="link-button" onClick={() => setExpandedYearId(expandedYearId === fy.id ? null : fy.id)}>{expandedYearId === fy.id ? 'Hide Periods' : 'View Periods'}</button>
                    {fy.status === 'open' && <IconActionButton icon={Lock} label="Close Year" onClick={() => closeYearMutation.mutate(fy.id)} />}
                  </span>
                ),
              },
            ]}
          />
        )}
      </section>

      {expandedYearId && yearDetailQuery.data && (
        <section className="card">
          <h2>Accounting Periods - {yearDetailQuery.data.data.year_label}</h2>
          <DataTable
            rows={yearDetailQuery.data.periods}
            rowKey={(p) => p.id}
            emptyMessage="No periods."
            columns={[
              { header: 'Period', render: (p) => p.period_label },
              { header: 'Start', render: (p) => p.start_date },
              { header: 'End', render: (p) => p.end_date },
              { header: 'Status', render: (p) => <StatusBadge status={p.status} /> },
              {
                header: '',
                render: (p) => (
                  <span className="icon-action-group">
                    {p.status === 'open' && <IconActionButton icon={Lock} label="Close" onClick={() => closePeriodMutation.mutate(p.id)} />}
                    {p.status === 'closed' && canReopen && <IconActionButton icon={Unlock} label="Reopen" onClick={() => reopenPeriodMutation.mutate(p.id)} />}
                  </span>
                ),
              },
            ]}
          />
        </section>
      )}
    </div>
  );
}
